import { useQueryClient } from "@tanstack/react-query";

import {
  getGetFirstAdStateQueryKey, useGetFirstAdState, useSaveFirstAdPick,
  useAskFirstAdScript, useApproveFirstAdScript, useAdvanceFirstAdCast, useStartFirstAdVideo,
  useAcknowledgeFirstAdPreview,
  useRedeemFirstAdOffer, useRetryFirstAdOffer,
} from "../../api/generated/endpoints/generation/generation";
import type { FirstAdPick, FirstAdState } from "../../api/generated/model";
import { soonest, urlRefreshIn, urlsDue } from "../../api/signed-urls";
import type { FirstAdDestination } from "../../platform/first-ad";
import { useStreamStatus } from "../../platform/stream-status";
import type { CreationPhase } from "./FirstAdCreation";

/** Display progress follows server state; it does not decide retry, spend, or app access. */
export function creationPhase(state: FirstAdState): CreationPhase {
  if (state.stage === "pick") return state.pick?.product ? "actor" : "product";
  if (state.preview_mode === "video" && state.run_job_id && !state.first_look_seen) return "keyframes";
  return state.stage;
}

export function useFirstAdFlow(destination: FirstAdDestination) {
  const { teamId, brandId, templateId } = destination;
  const client = useQueryClient();
  const stream = useStreamStatus();
  const key = getGetFirstAdStateQueryKey(teamId, brandId, templateId);
  const refresh = () => client.invalidateQueries({ queryKey: key });
  const state = useGetFirstAdState(teamId, brandId, templateId, { query: {
    refetchInterval: (query) => soonest(urlRefreshIn(query.state.data?.urls_expire_at),
      stream !== "live" && query.state.data?.working ? 15000 : false),
    refetchOnWindowFocus: (query) => urlsDue(query.state.data?.urls_expire_at) ? "always" : false,
  } });
  const accept = (value: FirstAdState, target: FirstAdDestination) => client.setQueryData(
    getGetFirstAdStateQueryKey(target.teamId, target.brandId, target.templateId), value);
  const save = useSaveFirstAdPick({ mutation: { onSuccess: accept } });
  const ask = useAskFirstAdScript({ mutation: { onSuccess: accept } });
  const approve = useApproveFirstAdScript({ mutation: { onSuccess: accept } });
  const advance = useAdvanceFirstAdCast({ mutation: { onSuccess: accept } });
  const seen = useAcknowledgeFirstAdPreview({ mutation: { onSuccess: accept } });
  const offer = useRedeemFirstAdOffer({ mutation: { onSuccess: accept } });
  const retry = useRetryFirstAdOffer({ mutation: { onSuccess: accept } });
  const video = useStartFirstAdVideo({ mutation: { onSuccess: (_job, variables) => client.invalidateQueries({
    queryKey: getGetFirstAdStateQueryKey(variables.teamId, variables.brandId, variables.templateId),
  }) } });
  const target = { teamId, brandId, templateId };
  const mutations = [save, ask, approve, advance, video, seen, offer, retry];
  return {
    state, refresh, phase: state.data ? creationPhase(state.data) : "product" as CreationPhase,
    pending: mutations.some((mutation) => mutation.isPending),
    error: mutations.find((mutation) => mutation.isError)?.error,
    save: (data: FirstAdPick) => save.mutateAsync({ ...target, data }),
    ask: async (data: FirstAdPick) => {
      // Only an explicit Continue redeems. Mounting, reading and autosaving never do.
      if (!state.data?.offer) await offer.mutateAsync({ ...target, data: { offer_code: "first-ad" } });
      return ask.mutateAsync({ ...target, data });
    },
    retry: () => retry.mutate({ ...target, data: { failed_attempt: 1 } }),
    approve: (lines: string[]) => approve.mutate({ ...target, data: { lines } }),
    advance: () => advance.mutate(target),
    start: () => video.mutate(target),
    acknowledgePreview: (run_job_id: string) => seen.mutate({ ...target, data: { run_job_id } }),
  };
}
